import { handlebars } from "common/helpers/Handlebars";
import { MenuItemRecord } from "common/records/MenuItem";
import { SectionRecord } from "common/records/Section";
import { RootState } from "common/reducers";
import { ids } from "common/selectors/idsManagement.selectors";
import { image } from "common/selectors/image.selectors";
import { currentUserOrgId } from "common/selectors/user.selectors";
import { Nullable } from "common/types";
import { List, Map, getIn } from "immutable";
import { createSelector } from "reselect";
import { fromJSON as dropdownFromJSON } from "common/components/Dropdown/Dropdown";
import { JobStateInfoRecord } from "common/records/JobState/JobStateInfo";
import { presentationMode } from "common/selectors/common.selectors";
import { currentEstimateGroups } from "common/selectors/estimateGroup.selectors";
import { isVisible } from "common/records/EstimateLineItem";
import { byId as estimateLineItemById } from "common/selectors/estimateLineItem.selectors";
import { activatedPriceListForCurrentEstimate } from "common/selectors/activatedPriceList.common.selectors";
import { CommissionStepRecord } from "common/records/CommissionStep";
import { currentOrgId } from "common/selectors/org.common.selectors";
import { ContractLanguageRecord } from "common/records/ContractLanguage";
import { CommonTitles } from "common/helpers/Format";
import { customForm, jobResultingCustomForm } from "common/selectors/customForm.selectors";
import { JobResultingConfigRecord } from "common/records/JobResultingConfig";
import { extractRjsfSchema, extractRjsfUISchema, JobResultingOptionRecord } from "common/records/JobResultingOption";
import { RJSFSchema, UiSchema } from "@rjsf/utils";
import _ from "lodash";
import { PreferenceAclValues } from "common/records/Preference";
export { currentOrgId } from "common/selectors/org.common.selectors";

const rootKey = "orgs";

/**
 * Loads byId key from orgs
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Map<number, OrgRecord>} Map<number, OrgRecord>
 */
export const byId = (state: RootState) => state.getIn([rootKey, "byId"]);

/**
 * Loads lastSavedById key from orgs
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Map<number, OrgRecord>} Map<number, OrgRecord>
 */
export const lastSavedById = (state: RootState) => state.getIn([rootKey, "lastSavedById"]);

/**
 * Loads the current org using currentOrgId
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {OrgRecord} OrgRecord
 */
export const currentOrg = createSelector([currentOrgId, byId], (orgId, byId) => {
  if (!orgId) {
    return null;
  }
  return byId.get(orgId);
});

/**
 * Loads the current org error
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Nullable<string>} Nullable<string>
 */
export const currentOrgErrors = (state: RootState) => currentOrg(state)?.errors;

/**
 * Loads the org using orgId prop
 *
 * @param {RootState} state The RootState
 * @param {{orgId: number}} options
 * @returns {OrgRecord} OrgRecord
 */
export const org = (state: RootState, props: { orgId: Nullable<number> }) => {
  if (!props.orgId) {
    return null;
  }
  return byId(state).get(props.orgId);
};

/**
 * Loads the current org using currentUserOrgId
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {OrgRecord} OrgRecord
 */
export const currentUserOrg = createSelector([currentUserOrgId, byId], (orgId, byId) => {
  if (!orgId) {
    return null;
  }
  return byId.get(orgId);
});

/**
 * Loads the current org using currentOrgId
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {OrgRecord} OrgRecord
 */
export const lastSavedCurrentOrg = createSelector([currentOrgId, lastSavedById], (orgId, lastSavedById) => {
  if (!orgId) {
    return null;
  }
  return lastSavedById.get(orgId);
});

/**
 * Loads the current org using currentOrgId and returns org.loading
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {boolean} loading
 */
export const currentOrgLoading = createSelector([currentOrg], (org) => {
  if (!org) return true;

  return org.loading;
});

/**
 * Loads the current org using currentOrgId and returns org.name
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {string} name
 */
export const currentOrgName = createSelector([currentOrg], (org) => {
  return org?.name;
});

/**
 * Loads the preferences config with a path (if provided) using currentOrgId
 *
 * @param {RootState} state The RootState
 * @param {{orgId: number, path: string[], notSet?: any}} options
 * @returns {string | number} preferences config
 */
export const currentOrgPrefConfig = createSelector([currentOrg, (_, props: any) => props], (org, props) => {
  return getIn(org, ["preferences", "config"].concat(props.path || []), props.notSet || undefined);
});

export const currentOrgDynamicPresentationsEnabled = (state: RootState): boolean =>
  currentOrgPrefConfig(state, { path: ["presentations", "dynamic_enabled"] }) === true;

/**
 * Loads the preferences config with estimator path using currentOrgId
 *
 * @param {RootState} state The RootState
 * @param {{ path: string[]; notSet?: string }} props The config path segments and optional default
 * @returns {string}
 */
export const currentOrgPrefEstimatorConfig = (state: RootState, props: { path: string[]; notSet?: string }) =>
  currentOrgPrefConfig(state, {
    path: ["estimator", ...props.path],
    notSet: props.notSet || "never",
  });

/**
 * Loads the preferences config with markup path using currentOrgId
 *
 * @param {RootState} state The RootState
 * @param {{path: string[], notSet?: any}} options
 * @returns {string} string
 */
export const currentOrgPrefMarkupConfig = (state: RootState, props: { name: string; notSet: string | number }) =>
  currentOrgPrefConfig(state, { path: ["markup", props.name], notSet: props.notSet ?? "never" });

/**
 * Uses presentationMode to determine if to show pricing details base on the config
 *
 * @param {RootState} state The RootState
 * @param {{name: string}} options
 * @returns {boolean} boolean
 */
export const estimatorConfigPricingDetails = createSelector(
  [currentOrgPrefEstimatorConfig, presentationMode],
  (config, navDisplay) => {
    if (navDisplay) {
      return ["always", "customer_only"].includes(config);
    }
    return ["always", "company_only"].includes(config);
  },
);

/**
 * Resolves a 10-value detailed-column enum against presentation mode and group type.
 * Used for `show_detailed_unit_price` and `show_detailed_pricing`.
 *
 * @param {RootState} state
 * @param {{ path: string[]; groupType: "main" | "option" }} props
 * @returns {boolean}
 */
export const estimatorConfigDetailedColumn = createSelector(
  [
    (state: RootState, props: { path: string[]; groupType: "main" | "option" }) =>
      currentOrgPrefEstimatorConfig(state, { path: props.path, notSet: "never" }),
    (state: RootState) => presentationMode(state),
    (_: RootState, props: { path: string[]; groupType: "main" | "option" }) => props.groupType,
  ],
  (config, navDisplay, groupType) => {
    if (navDisplay) {
      if (groupType === "option") {
        return ["always", "customer_only", "company_customer_option", "customer_option_only"].includes(config);
      }
      return ["always", "customer_only", "company_customer_main", "customer_main_only"].includes(config);
    } else {
      if (groupType === "option") {
        return [
          "always",
          "company_only",
          "company_option_only",
          "company_customer_main",
          "company_customer_option",
        ].includes(config);
      }
      return [
        "always",
        "company_only",
        "company_main_only",
        "company_customer_main",
        "company_customer_option",
      ].includes(config);
    }
  },
);

/**
 * Checks if the config is set to always show estimated dates,
 * defaulting to "never" if not set.
 *
 * @param {RootState} state
 * @returns {boolean}
 */
export const estimatorConfigEstimatedDates = (state: RootState): boolean =>
  currentOrgPrefEstimatorConfig(state, {
    path: ["show_estimated_dates"],
    notSet: "never",
  }) === "always";

/**
 * Returns true when pricing columns (show_detailed_unit_price / show_detailed_pricing)
 * must be force-hidden due to hidden products or hidden line items in the current estimate.
 * Only applies in presentation mode; always returns false in company mode.
 * Matches rsf-app Org.showDetailedUnitPrice() / Org.showDetailedPricing().
 *
 * @param {RootState} state The RootState
 * @returns {boolean}
 */
export const estimatorHiddenProductsOverride = createSelector(
  [presentationMode, activatedPriceListForCurrentEstimate, currentEstimateGroups, estimateLineItemById],
  (navDisplay, apl, groups, lineItemsById) => {
    if (!navDisplay) return false;
    if (apl?.hidden_products) return true;
    // Mirrors rsf-app Estimate.hidden_line_items(): any line item not visible in company mode
    // (i.e. "never"/"a"/"b"/"ab") forces the pricing columns off.
    return groups.some((group) =>
      (group.line_item_ids || List()).some((id: number) => {
        const item = lineItemsById.get(id);
        if (!item || item._destroy) return false;
        return !isVisible(item, false);
      }),
    );
  },
);

/**
 * Loads the settings acl with a path (if provided) using currentOrgId. The resolved shape depends
 * on the path: a leaf path (e.g. `["flags"]`, `["estimator"]`) resolves to that key's string list —
 * a `List<string>`, the shape this selector is annotated for — while an empty path (`[]`, as `OrgCan`
 * uses it) resolves to the whole acl `Map` and a non-leaf path to a nested value. Callers passing a
 * non-leaf path must treat the result as the corresponding shape. When the org or path is absent the
 * caller-provided `notSet` fallback is returned (callers pass `List()` for an empty-list default),
 * falling back to `undefined` when no `notSet` was given.
 *
 * @param {RootState} state The RootState
 * @param {{orgId?: number, path?: string | string[], notSet?: unknown}} options The acl path segments
 *   (leaf or nested) and an optional fallback returned when the value is unset
 * @returns {List<string> | undefined} The string-list ACL at a leaf path, the `notSet` fallback, or
 *   undefined when unset and no fallback was provided
 */
export const currentOrgSettingsAcl = createSelector(
  [currentOrg, (_: RootState, props: { orgId?: number; path?: string | string[]; notSet?: unknown } = {}) => props],
  (org, props): List<string> | undefined => {
    return getIn(org, ["settings", "acl"].concat(props.path || []), props.notSet || undefined);
  },
);

const SSO_FLAGS_PATH = { path: ["flags"] };

/**
 * Returns true if the current org has auth0 in its SSO flags ACL
 *
 * @param {RootState} state The RootState
 * @returns {boolean} boolean
 */
export const isAuth0Enabled = (state: RootState): boolean =>
  currentOrgSettingsAcl(state, SSO_FLAGS_PATH)?.includes("auth0") || false;

/**
 * Returns true if the current org has okta in its SSO flags ACL
 *
 * @param {RootState} state The RootState
 * @returns {boolean} boolean
 */
export const isOktaEnabled = (state: RootState): boolean =>
  currentOrgSettingsAcl(state, SSO_FLAGS_PATH)?.includes("okta") || false;

/**
 * Loads the settings acl with a path (if provided) using currentUserOrgId. Mirrors
 * `currentOrgSettingsAcl` but resolves against the current user's org: a leaf path resolves to that
 * key's `List<string>`, an empty path (`[]`, as `OrgCan` uses it) to the whole acl `Map`. When the
 * org or path is absent the caller-provided `notSet` fallback is returned, falling back to
 * `undefined` when none was given.
 *
 * @param {RootState} state The RootState
 * @param {{orgId?: number, path?: string | string[], notSet?: unknown}} options The acl path segments
 *   and an optional fallback returned when the value is unset
 * @returns {List<string> | undefined} The string-list ACL at a leaf path, the `notSet` fallback, or
 *   undefined when unset and no fallback was provided
 */
export const currentUserOrgSettingsAcl = createSelector(
  [currentUserOrg, (_: RootState, props: { orgId?: number; path?: string | string[]; notSet?: unknown } = {}) => props],
  (org, props) => {
    return getIn(org, ["settings", "acl"].concat(props.path || []), props.notSet || undefined);
  },
);

/**
 * Loads the settings config with a path (if provided) using currentOrgId
 *
 * @param {RootState} state The RootState
 * @param {{orgId: number, path: string[], notSet?: any}} options
 * @returns {string | number} preferences config
 */
export const currentOrgSettingsConfig = createSelector([currentOrg, (_, props) => props], (org, props) => {
  return getIn(org, ["settings", "config"].concat(props.path || []), props.notSet || undefined);
});

/**
 * Loads the settings config with a path (if provided) using currentUserOrgId
 *
 * @param {RootState} state The RootState
 * @param {{orgId: number, path: string[], notSet?: any}} options
 * @returns {string | number} preferences config
 */
export const currentUserOrgSettingsConfig = createSelector([currentUserOrg, (_, props) => props], (org, props) => {
  return getIn(org, ["settings", "config"].concat(props.path || []), props.notSet || undefined);
});

/**
 * Loads the preferences acl with a path (if provided) using currentOrgId
 *
 * @param {RootState} state The RootState
 * @param {{orgId: number, path: string[], notSet?: any}} options
 * @returns {string | number} preferences acl
 */
export const currentOrgPrefAcl = createSelector([currentOrg, (_, props) => props], (org, props) => {
  return getIn(org, ["preferences", "acl"].concat(props.path || []), undefined);
});

/**
 * Loads the preferences acl with a path (if provided) using currentUserOrgId
 *
 * @param {RootState} state The RootState
 * @param {{orgId: number, path: string[], notSet?: any}} options
 * @returns {string | number} preferences config
 */
export const currentUserOrgPrefAcl = createSelector([currentUserOrg, (_, props) => props], (org, props) => {
  return getIn(org, ["preferences", "acl"].concat(props.path || []), undefined);
});

export const currentOrgDocumentAclIncludes = (state: RootState, props: { value: PreferenceAclValues }): boolean => {
  const documentAcl: Nullable<List<PreferenceAclValues>> = currentOrgPrefAcl(state, { path: ["document"] });
  return documentAcl?.includes(props.value) || false;
};

/**
 * Loads the current user org name
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {string} string
 */
export const currentUserOrgName = (state: RootState) => currentUserOrg(state)?.get("name") || "";

/**
 * Loads the current user org's logo image record
 * Finds the first image of the org
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {ImageRecord} ImageRecord
 */
export const currentUserOrgLogo = createSelector([currentUserOrg, (state: RootState) => state], (org, state) => {
  return image(state, { imageId: org?.logo_id });
});

/**
 * Loads the current user descendants count
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {number} number
 */
export const currentUserOrgDescendantsCount = (state: RootState) =>
  currentUserOrg(state)?.get("descendants_count") || 0;

/**
 * Loads the org's statuses using the current orgId.
 * Includes org.status as it is not included in org.events, grabbing it from lastSavedById to avoid duplication.
 *
 * @param {RootState} state The RootState
 * @param {{orgId: number}} options
 * @returns {List<string>} List of statuses
 */
export const currentOrgStatuses = createSelector([lastSavedCurrentOrg], (org) => {
  const currentStatus = org?.get("status");
  if (!org || !currentStatus) {
    return List() as List<string>;
  }

  return org.get("events");
});

/**
 * Loads the current org's logo image record
 * Finds the first image of the org
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {ImageRecord} ImageRecord
 */
export const currentOrgLogo = createSelector([currentOrg, (state: RootState) => state], (org, state) => {
  return image(state, { imageId: org?.logo_id });
});

/**
 * Loads the current org's logo image record
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {ImageRecord} ImageRecord
 */
export const currentOrgCoverImage = createSelector([currentOrg, (state: RootState) => state], (org, state) => {
  return image(state, { imageId: org?.default_cover_image_id });
});

/**
 * Loads the current org's logo image record
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {ImageRecord} ImageRecord
 */
export const currentOrgSignatureEmailImage = createSelector([currentOrg, (state: RootState) => state], (org, state) => {
  return image(state, { imageId: org?.custom_signature_email_image_id });
});

/**
 * Loads the current org's root folder id by name
 *
 * @param {RootState} state The RootState
 * @param {{ folderName }} options
 * @returns {number} number
 */
export const currentOrgRootFolderId = createSelector([currentOrg, (_state, props) => props], (org, props) => {
  return org?.getIn(["folders", props.folderName]);
});

/**
 * Loads the current org's root folder id by name
 *
 * @param {RootState} state The RootState
 * @param {{ folderName }} options
 * @returns {number} number
 */
export const orgOverviewIds = (state: RootState) => ids(state, { modelName: "orgOverviews" }) as List<number>;

/**
 * Loads the available job menus
 * Returns the available job menus for the current org
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {List<string>} List of job menus available
 */
export const currentOrgAvailableJobMenus = createSelector([currentOrg], (org) => {
  if (!org) {
    return List([]);
  }

  return org.getIn(["settings", "config", "job_menu", "enabled"], List([])) as List<string>;
});

/**
 * Loads the current org job menu sections
 * Returns the job menu sections structure for the current org
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {List<SectionRecord>} List of job menu sections
 */
export const currentOrgJobMenuSections = createSelector([currentOrg], (org) => {
  if (!org) {
    return List<SectionRecord>();
  }

  return org.getIn(["preferences", "config"]).job_menu.sections;
});

/**
 * Loads the current org job menu item ids
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {List<string>} List of job menu item ids
 */
export const currentOrgJobMenuItems = createSelector([currentOrgJobMenuSections], (sections) => {
  return sections.flatMap((section) => section.menuItems).map((menuItem) => menuItem.id);
});

/**
 * Loads the current org section available job menus
 * Returns the job menus that are not in any section for the current org
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {List<any>} List of job menu sections
 */
export const currentOrgSectionAvailableJobMenus = createSelector(
  [currentOrgAvailableJobMenus, currentOrgJobMenuSections],
  (menus, sections) => {
    return menus
      .filter((menu) => !sections.find((section) => !!section.menuItems.find((item) => item.id === menu)))
      .sort();
  },
);

/**
 * Loads the current menuItem for the provided id
 * Returns the menu item from the right section based on the provided id
 *
 * @param {RootState} state The RootState
 * @param {{id: String}} options
 * @returns {Nullable<MenuItemRecord>} MenuItemRecord or null
 */
export const menuItem = createSelector([currentOrgJobMenuSections, (_state, props) => props], (sections, props) => {
  let foundMenuItem: Nullable<MenuItemRecord> = null;
  for (const section of sections) {
    const menuItem = section?.get("menuItems").find((mi) => mi.id === props.id);
    if (menuItem) {
      foundMenuItem = menuItem;
    }
  }
  return foundMenuItem;
});

/**
 * Returns the defined title for a document type (singular or plural)
 *
 * @param {RootState} state The RootState
 * @param { { documentType: string; plural?: boolean }} props
 * @returns {string} title
 */
export const getTitle = (
  state: RootState,
  props: {
    documentType: "contract" | "estimate" | "opening_name" | "estimator" | "inspection";
    plural?: boolean;
    lowerCase?: boolean;
  },
) => {
  const titles = currentOrgPrefConfig(state, { path: ["titles"] });

  return setTitle(titles, props);
};

const setTitle = (
  titles: Map<any, any>,
  props: {
    documentType: "contract" | "estimate" | "opening_name" | "estimator" | "inspection";
    plural?: boolean;
    lowerCase?: boolean;
  },
) => {
  const { documentType, plural, lowerCase } = props;
  const title = titles ? titles.get(documentType) : null;
  let val = "";

  switch (documentType) {
    case "contract":
      val = title || "Agreement";
      break;
    case "estimate":
      val = title || "Proposal";
      break;
    case "opening_name":
      val = title || "Name";
      break;
    case "estimator":
      val = title || "Estimate";
      break;
    case "inspection":
      val = title || "Inspection";
      break;
    default:
  }

  if (plural) {
    val = `${val}s`;
  }

  if (lowerCase) {
    val = val.toLowerCase();
  }

  return val;
};

/**
 * Returns the defined estimate title (singular or plural)
 *
 * @param {RootState} state The RootState
 * @param { { plural?: boolean; lowerCase?: boolean }} props
 * @returns {string} title
 */
export const estimateTitle = (
  state: RootState,
  { plural, lowerCase }: { plural?: boolean; lowerCase?: boolean },
): string => {
  return getTitle(state, { documentType: "estimate", plural, lowerCase });
};

/**
 * Returns the defined contract title (singular or plural)
 *
 * @param {RootState} state The RootState
 * @param { { plural?: boolean; lowerCase?: boolean }} props
 * @returns {string} title
 */
export const contractTitle = (
  state: RootState,
  { plural, lowerCase }: { plural?: boolean; lowerCase?: boolean },
): string => {
  return getTitle(state, { documentType: "contract", plural, lowerCase });
};

/**
 * Returns the defined inspection title (singular or plural)
 *
 * @param {RootState} state The RootState
 * @param { { plural?: boolean; lowerCase?: boolean }} props
 * @returns {string} title
 */
export const inspectionTitle = (
  state: RootState,
  { plural, lowerCase }: { plural?: boolean; lowerCase?: boolean },
): string => {
  return getTitle(state, { documentType: "inspection", plural, lowerCase });
};

/**
 * Returns the defined estimator title (singular or plural)
 *
 * @param {RootState} state The RootState
 * @param { { plural?: boolean; lowerCase?: boolean }} props
 * @returns {string} title
 */
export const estimatorTitle = (
  state: RootState,
  { plural, lowerCase }: { plural?: boolean; lowerCase?: boolean },
): string => {
  return getTitle(state, { documentType: "estimator", plural, lowerCase });
};

/**
 * Singular estimator title for the current org. Memoized via createSelector —
 * only recomputes when the org's titles config changes.
 *
 * @param {RootState} state
 * @returns {string} title
 */
export const currentOrgEstimatorTitle = createSelector(
  [(state: RootState) => currentOrgPrefConfig(state, { path: ["titles"] })],
  (titles) => setTitle(titles, { documentType: "estimator" }),
);

export const currentOrgTotalTitle = createSelector(
  [(state: RootState) => currentOrgPrefConfig(state, { path: ["titles", "total"] })],
  (title: string | undefined) => title || CommonTitles("total"),
);

export const currentOrgInvestmentTitle = createSelector(
  [(state: RootState) => currentOrgPrefConfig(state, { path: ["titles", "finance"] })],
  (title: string | undefined) => title || CommonTitles("finance"),
);

export const currentOrgFinanceOptionsTitle = createSelector(
  [(state: RootState) => currentOrgPrefConfig(state, { path: ["titles", "finance_options"] })],
  (title: string | undefined) => title || CommonTitles("finance_options"),
);

export const currentOrgPricePresentationTitle = createSelector(
  [(state: RootState) => currentOrgPrefConfig(state, { path: ["titles", "price_presentation"] })],
  (title: string | undefined) => title || CommonTitles("price_presentation"),
);

/**
 * Returns the data needed for handlebars for sections
 *
 * @param {RootState} state The RootState
 * @param { { plural?: boolean; lowerCase?: boolean }} props
 * @returns {hash{titles}} titles
 */
export const jobSectionTemplate = createSelector(
  [(state) => currentOrgPrefConfig(state, { path: ["titles"] }), currentOrgName],
  (titles, org_name) => {
    return {
      estimates_title: setTitle(titles, { documentType: "estimate", plural: true }),
      estimate_title: setTitle(titles, { documentType: "estimate", plural: false }),
      contracts_title: setTitle(titles, { documentType: "contract", plural: true }),
      contract_title: setTitle(titles, { documentType: "contract", plural: false }),
      estimators_title: setTitle(titles, { documentType: "estimator", plural: true }),
      estimator_title: setTitle(titles, { documentType: "estimator", plural: false }),
      org_name,
    };
  },
);

export const sectionRenderedLabel = createSelector(
  [jobSectionTemplate, (_state, props: { label: string }) => props.label],
  (jobSectionTemplate, label) => {
    return handlebars.compile(label, jobSectionTemplate);
  },
);

/**
 * Pulls `org_count` from the current org
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {number} ImageRecord
 */
export const currentOrgChildOrgsCount = createSelector([currentOrg], (org) => org?.org_count);

/**
 * Loads overviewsById key from orgs
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Map<number, OrgOverviewRecord>} Map<number, OrgOverviewRecord>
 */
export const overviewsById = (state: RootState) => state.getIn([rootKey, "overviewsById"]);

/**
 * Loads OrgOverview from overviewsById based on provided orgId
 *
 * @param {RootState} state The RootState
 * @param {{orgId: number}} options
 * @returns {OrgOverviewRecord} OrgOverviewRecord
 */
export const orgOverview = (state: RootState, props: { orgId: Nullable<number> }) => {
  if (!props.orgId) {
    return null;
  }

  return overviewsById(state).get(props.orgId);
};

/**
 * Loads the the lead sources dropdown for the org by orgId
 *
 * @param {RootState} state The RootState
 * @param {{orgId: number}} options
 * @returns {OrgRecord} OrgRecord
 */
export const orgLeadSourceDropdown = createSelector([org], (org) => {
  return (org?.get("lead_sources") || List())
    .map((ls) => dropdownFromJSON({ id: ls, name: ls }))
    .unshift(dropdownFromJSON({ id: null, name: "All Lead Sources" }));
});

/**
 * Loads the the job types dropdown for the org by orgId
 *
 * @param {RootState} state The RootState
 * @param {{orgId: number}} options
 * @returns {OrgRecord} OrgRecord
 */
export const orgJobTypeDropdown = createSelector([org], (org) => {
  return (org?.get("job_types") || List()).unshift(dropdownFromJSON({ id: null, name: "All Job Types" }));
});

/**
 * Loads the the job states dropdown for the org by orgId
 *
 * @param {RootState} state The RootState
 * @param {{orgId: number}} options
 * @returns {OrgRecord} OrgRecord
 */
export const orgJobStatesDropdown = createSelector([org], (org) => {
  const stateInfo = org?.getIn(["job_states", "state_info"]) || Map<string, JobStateInfoRecord>();
  return List(stateInfo.keys()).map((key) =>
    dropdownFromJSON({ id: key, name: stateInfo.get(key)?.get("pretty_name") }),
  );
});

/**
 * Returns price list id for current activated price list of org
 *
 * @param {RootState} state The RootState
 * @returns {number}
 */
export const currentOrgPriceListId = createSelector([currentOrg], (org) => org?.current_price_list_id);

/**
 * Loads the org with orgId passed in props logo
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {ImageRecord} ImageRecord
 */
export const orgOverviewLogo = createSelector([orgOverview, (state: RootState) => state], (org, state) => {
  return image(state, { imageId: org?.logo_id });
});

/**
 * Loads the current org's custom commission calculator config
 * @param state The RootState
 * @returns {Map<string, any>} The custom commission calculator config
 */
export const commissionCustomConfig = (state: RootState) =>
  currentOrgSettingsConfig(state, { path: ["estimator", "commission_calculator", "custom_config"] });

/**
 * Loads commission steps in the current org's custom commission calculator config
 * @param state The RootState
 * @returns {List<string>} List of commission step IDs
 */
export const customCommissionStepIds = createSelector(
  [commissionCustomConfig],
  (config: Map<string, any>) =>
    config?.get<List<CommissionStepRecord>>("steps", List()).map((step: CommissionStepRecord) => step.id) || List(),
);

/**
 * Loads a commission step in the current org's custom commission calculator config by the given id
 * @param state The RootState
 * @param {{ id: string }} props The props containing the id of the commission step
 * @returns {CommissionStepRecord | null} The commission step record or null if not found
 */
export const customCommissionStep = createSelector(
  [commissionCustomConfig, (_state: RootState, props: { id: string }) => props.id],
  (config, id) => {
    const steps = config?.get("steps", List());
    return steps?.find((step: CommissionStepRecord) => step.id === id) || null;
  },
);

/**
 * Loads the org overviews by the IDs from idsManagement -> orgOverviews
 */
export const orgOverviews = createSelector([orgOverviewIds, overviewsById], (ids, byId) => {
  return ids.map((id) => byId.get(id)).filter((org) => !!org);
});

/**
 * Loads the current org's contract Languages List
 *
 * @param {RootState} state The RootState
 * @returns {List<ContractLanguageRecord>} List of contract language record
 */
export const currentOrgContractLanguages = createSelector(
  [currentOrg],
  (org) => org?.get("contract_languages") || (List() as List<ContractLanguageRecord>),
);

/**
 * Loads the current orgs job resulting configuration.
 *
 * @param {RootState} state The RootState
 * @returns {JobResultingConfigRecord | undefined} Job resulting config for the org
 */
export const jobResultingOrgPrefConfig = (state: RootState): Nullable<JobResultingConfigRecord> => {
  return currentOrgPrefConfig(state, { path: ["job_resulting"] });
};

/**
 * Loads the job resulting options in the config that matches the custom form's
 * form data result.
 *
 * @param {RootState} state the RootState
 * @param {{ id: string }} props The custom form ID to load the option for
 * @returns {JobResultingOptionRecord | undefined} Job resulting record in OCC format
 */
export const jobResultingOption = createSelector(
  [jobResultingOrgPrefConfig, customForm],
  (config, customForm): JobResultingOptionRecord | undefined => {
    return config?.get("options")?.find((o) => o?.get("name") === customForm?.getIn(["form_data", "result"]));
  },
);

/**
 * Loads the job resulting option and coverts the data to a structure that RSJF expects.
 *
 * @param {RootState} state the RootState
 * @param {{ id: string }} props The custom form ID to load the option for
 * @returns {RJSFSchema | null} Configured with RSJF schema associated with the option
 */
export const jobResultingOptionFormSchema = createSelector([jobResultingOption], (option): RJSFSchema | null => {
  if (!option) {
    return null;
  }

  return extractRjsfSchema(option);
});

/**
 * Loads the job resulting option and coverts the data to a structure that RSJF
 * expects for the UI layout.
 *
 * @param {RootState} state the RootState
 * @param {{ id: string | -1 }} props The custom form ID to load the option for
 * @returns {UiSchema | null} Configured with RSJF UI schema associated with the option
 */
export const jobResultingOptionFormUiSchema = createSelector([jobResultingOption], (option): UiSchema | null => {
  if (!option) {
    return null;
  }

  return extractRjsfUISchema(option);
});

/**
 * Loads the job resulting, UI Schema, and field schema from state. Using
 * this data, a UiSchema is returned which only represents the fields with
 * values present.
 *
 * @param {RootState} state The RootState
 * @param {{id: string | -1}} props The custom form ID to load
 * @returns {UiSchema | null} Configured with RSJF UI schema associated with
 * this option for field values which are present.
 */
export const jobResultingFormReadOnlyUiSchema = createSelector(
  [jobResultingCustomForm, jobResultingOptionFormUiSchema, jobResultingOptionFormSchema],
  (customForm, fullUiSchema, schema): UiSchema | null => {
    if (!customForm || !fullUiSchema || !schema?.properties) {
      return null;
    }

    // Update the UISchema to only show fields with values
    const readOnlyUiSchema = _.cloneDeep(fullUiSchema);
    for (const key of Object.keys(schema.properties)) {
      const value = customForm.form_data.get(key);

      let hide = false;
      if (value === null || value === undefined) {
        hide = true;
      } else if (typeof value === "string") {
        hide = !value.trim();
      } else if (Array.isArray(value)) {
        hide = value.length === 0;
      } else if (List.isList(value)) {
        hide = value.size === 0;
      }

      if (hide) {
        readOnlyUiSchema[key] = { "ui:widget": "hidden" };
      }
    }

    return readOnlyUiSchema;
  },
);
